'use client';

import React, { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { BgChatRoom, FarmingAiriIcon } from '@/assets';
import { useSocket } from '@/provider/SocketProvider';
import { useForm } from 'react-hook-form';

import RenderWhen from '../../../components/RenderWhen';
import ItemChat from './ItemChat';
import SendMessage from './SendMessage';
import useGroupChat from './useGroupChat';
import { useSQuery } from '@/utils';
import { getModels } from '@/services';
import { baseSdxlModel, baseText2Model } from '@/configs/constant';

const GroupChat: React.FC<{}> = () => {
  const [isLoadingFirstTime, setIsLoadingFirstTime] = useState(true);
  const [content, setContent] = useState('');
  const [listCommand, setListCommand] = useState<any[]>();
  const [listCommandDisplay, setListCommandDisplay] = useState<any[]>();
  const [selectMethodCommandLine, setSelectMethodCommandLine] = useState<any>(null);
  const scrollableDivRef = useRef<HTMLDivElement>(null);

  const refUseForm = useForm();
  const { list, setList, currentPage, loadMoreMessage, hasMore, addMoreItemMess } =
    useGroupChat(isLoadingFirstTime);
  console.log('🐉 ~ list ~ 🚀\n', list);

  const { socket } = useSocket();
  const { data: listModels } = useSQuery({
    queryKey: ['list-models'],
    queryFn: () => getModels(30, 0),
    keepPreviousData: true,
    cacheTime: 5000,
  });

  const listenNewMessEvent = () => {
    if (!socket) return;
    const event = 'chatAll';
    console.log('🐉 ~ listenNewMessEvent ~ event ~ 🚀\n', event);
    const eventGenerate = 'commandSuccess';
    socket.on(event, (message: any) => {
      const result = JSON.parse(message);
      console.log('🐉 ~ socket.on ~ message ~ 🚀\n', result);
      addMoreItemMess(result);
    });
    socket.on(eventGenerate, (message: any) => {
      const result = JSON.parse(message);
      console.log('🐉 ~ socket.on ~ generate ~ 🚀\n', result);
      if (result?.url) {
        setList((items) =>
          items.map((item) =>
            item.id === result.chatId ? { ...item, processingAsset: { url: result.url } } : item,
          ),
        );
      }
    });
  };

  const scrollToBottom = () => {
    if (scrollableDivRef.current) {
      scrollableDivRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const handleSelectCommand = (command: any) => {
    setSelectMethodCommandLine(command);
    setContent(command.command);
  };

  useEffect(() => {
    listenNewMessEvent();
  }, [socket]);

  useEffect(() => {
    setIsLoadingFirstTime(false);
  }, []);

  useEffect(() => {
    if (listModels?.data?.items) {
      setListCommand([
        {
          command: '/promptXL ',
          desc: `Generate artwork with
                ${listModels?.data?.items?.find((item: any) => item.type === 'SDXL').name}`,
          model: listModels?.data?.items?.find((item: any) => item.type === 'SDXL'),
        },
        {
          command: '/promptSD ',
          desc: `Generate artwork
                ${listModels?.data?.items?.find((item: any) => item.type === 'Text2Image').name}`,
          model: listModels?.data?.items?.find((item: any) => item.type === 'Text2Image'),
        },
      ]);
    }
  }, [listModels?.data?.items]);

  useEffect(() => {
    switch (selectMethodCommandLine?.model?.type) {
      case 'Text2Image':
        refUseForm.reset({ ...baseText2Model, width: 1024, height: 1024, size: '1024x1024' });
        break;
      case 'SDXL':
        refUseForm.reset(baseSdxlModel);
        break;
      default:
        refUseForm.reset(baseSdxlModel);
        break;
    }
  }, [selectMethodCommandLine]);

  return (
    <div className="h-[calc(100vh-60px)] xl:h-[calc(100vh-72px)] main-container flex max-h-[100vh] flex-col gap-8 pt-4 md:gap-3 md:p-3 md:pb-0 relative">
      <div className="flex flex-col gap-3 md:gap-1">
        <div className="text-[18px] font-semibold md:text-[16px]">Community Chats</div>
        <div
          className="relative h-[70vh] rounded-[17px] border-1 border-gray-200 py-[14px] pl-7 pr-5 md:px-3 md:py-2"
          style={{
            backgroundImage: `url(${BgChatRoom.src})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <RenderWhen>
            <RenderWhen.If isTrue={list.length > 0}>
              <div
                id="scrollableDiv"
                ref={scrollableDivRef}
                className="flex h-full flex-col-reverse overflow-y-auto"
                style={{ flexGrow: 1 }}
              >
                <InfiniteScroll
                  dataLength={list.length}
                  next={() => loadMoreMessage(currentPage + 1)}
                  hasMore={hasMore}
                  loader={<h4 className="flex self-center">Loading...</h4>}
                  scrollableTarget="scrollableDiv"
                  inverse
                  style={{ display: 'flex', flexDirection: 'column-reverse' }}
                >
                  <div id="list-message" className="scrollbar-custom flex flex-col-reverse">
                    {list.map((item, index) => (
                      <ItemChat
                        key={item.id}
                        message={item}
                        prevItem={(index < list.length ? list[index + 1] : list[0]) || item}
                        nextItem={(index > 0 ? list[index - 1] : list[list.length - 1]) || item}
                      />
                    ))}
                  </div>
                </InfiniteScroll>
              </div>
            </RenderWhen.If>
          </RenderWhen>
        </div>
      </div>
      <div
        className="w-[97%] self-center rounded-xl bg-white absolute bottom-[16px]"
        style={{ boxShadow: '0px 4px 8px 8px rgba(199, 205, 231, 0.25)' }}
        onClick={scrollToBottom}
      >
        {!selectMethodCommandLine && Array.from(content)[0] === '/' && (
          <div className="bg-white flex flex-col bottom-[50px] w-full z-50 border-b border-gray-100 rounded-t-xl">
            {listCommandDisplay?.map((command) => (
              <div
                onClick={() => handleSelectCommand(command)}
                className="flex items-center p-3 hover:bg-orange-50 cursor-pointer"
              >
                <FarmingAiriIcon />
                <div className="flex flex-col ml-3">
                  <span className="text-14 font-semibold">{command.command}</span>
                  <span className="text-14 font-normal">{command.desc}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="p-3">
          <SendMessage
            callback={() => {}}
            setContent={setContent}
            content={content}
            listCommand={listCommand}
            setListCommandDisplay={setListCommandDisplay}
            selectMethodCommandLine={selectMethodCommandLine}
            setSelectMethodCommandLine={setSelectMethodCommandLine}
            refUseForm={refUseForm}
          />
        </div>
      </div>
    </div>
  );
};

export default GroupChat;
